// The ONE render-boundary translation of a donor-info request channel into treasurer English.
// Engineer enum identifiers never reach treasurer copy (Finance/CLAUDE.md), and routing every
// surface through one map is what keeps the picker and the history list saying the same word
// for the same channel.

import { DonorInfoRequestChannel } from '../generated/enums.gen';
import { assertNever } from '../utils/assertNever';

/**
 * Both forms a channel is ever rendered in, side by side so they can't drift apart:
 * `noun` is what the picker offers, `past` is how the history list reports a completed act
 * ("Emailed 3 Mar" reads as something the treasurer did; "Email 3 Mar" reads as an instruction).
 */
interface ChannelCopy {
  noun: string;
  past: string;
}

function copyFor(channel: DonorInfoRequestChannel): ChannelCopy {
  switch (channel) {
    case DonorInfoRequestChannel.Email:
      return { noun: 'Email', past: 'Emailed' };
    case DonorInfoRequestChannel.Mail:
      return { noun: 'Mail', past: 'Mailed' };
    case DonorInfoRequestChannel.Phone:
      return { noun: 'Phone', past: 'Phoned' };
    case DonorInfoRequestChannel.InPerson:
      return { noun: 'In person', past: 'Asked in person' };
    default:
      // Exhaustive: a new channel must be given copy here rather than leaking its
      // identifier onto a treasurer's screen.
      return assertNever(channel);
  }
}

/** Treasurer copy for a channel, as the picker offers it. */
export function donorInfoRequestChannelLabel(channel: DonorInfoRequestChannel): string {
  return copyFor(channel).noun;
}

/** Past-tense copy for the request-history list. */
export function donorInfoRequestChannelPastLabel(channel: DonorInfoRequestChannel): string {
  return copyFor(channel).past;
}
